import { DataSource, DataSourceType } from "@coeff/api";
import { COLORS, DataSourceTypeDisplayNames } from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "antd";
import React, { useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { Button } from "../../../common/Button";
import { Card } from "../../../common/Card";
import { Checkbox } from "../../../common/Checkbox";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { Graphics } from "../../../common/Graphics";
import { ConnectPlugIcon, LinkedIn } from "../../../common/Icons";
import { LoaderWithPerfTimings } from "../../../common/Loader";
import { SecurityInfo } from "../../../common/SecurityInfo";
import { Steps } from "../../../common/Steps";
import { useApiClient, useEventManagerContext } from "../../../globals/contexts";
import { BaseAuthorizeLocationState } from "../../context/AuthContext";

const { Footer } = Layout;

const StyledContent = styled.div<{ $hidePageHeader: boolean }>`
  && {
    height: 100%;
    min-height: calc(100vh - 128px);
    margin-top: ${props => (props.$hidePageHeader ? "0" : "64px")};
    background-color: ${COLORS.white};
    padding: 14px 0px;

    .datasourceLogo {
      margin: auto;
      display: block;
    }

    .graphicsLogo {
      .datasourceLogo {
        width: 60px;
        height: 60px;
      }
    }
  }
`;

const StyledSteps = styled(Steps)`
  padding: 0px 16px;

  .ant-steps-item-container {
    .ant-steps-item-icon {
      display: none;
    }
  }

  .ant-steps-item-content {
    width: 100%;
  }

  .ant-steps-item-description {
    padding: 0px;
    width: 100%;
  }

  .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
    display: none !important;
  }

  .ant-steps-item-process > .ant-steps-item-container > .ant-steps-item-tail::after {
    display: none !important;
  }

  .ant-steps-item-wait > .ant-steps-item-container > .ant-steps-item-tail::after {
    display: none !important;
  }
`;

const StyledText = styled.div`
  text-align: center;
  margin: 0px;

  a {
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
  }
`;

const StyledAnchor = styled.a<{ fontWeight?: string | number }>`
  color: ${COLORS.coeblue4};
  font-weight: ${({ fontWeight }) => fontWeight || 600};
  font-size: 14px;
  line-height: 18px;
  :hover {
    color: ${COLORS.coeblue4};
  }
`;

const StyledButton = styled(Button)`
  margin-top: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const ConfirmCheckbox = styled(Checkbox)`
  && {
    margin: 12px 0px 0px 0px;
    display: flex;
    align-items: center;

    .ant-checkbox {
      top: -0.09em;
    }
  }
`;

const StyledStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.black10};
  padding: 12px;
  margin: 8px 0px;
  border-radius: 4px;
  justify-content: center;
`;

const StyledFooter = styled(Footer)`
  position: sticky;
  bottom: 0px;
  padding: 0;
  background: ${COLORS.white};
`;

export interface AuthorizeLinkedInLocationState extends BaseAuthorizeLocationState {
  dataSource?: DataSource;
  redirectOnBack?: string;
}

export interface AuthorizeLinkedInContentProps {
  dataSource?: DataSource;
  isEditConnectionId?: string;
  browserAgentExtensionUrl?: string;
  onComplete?: (dataSource: DataSource) => void;
}

const AuthorizeLinkedInContent: React.FC<AuthorizeLinkedInContentProps> = ({
  dataSource,
  isEditConnectionId = "",
  browserAgentExtensionUrl = "",
  onComplete,
}) => {
  const apiClient = useApiClient();
  const { track } = useEventManagerContext();

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isCreatingDataSource, setIsCreatingDataSource] = useState<boolean>(false);
  const [userConfirmExtensionInstalled, setUserConfirmExtensionInstalled] =
    useState<boolean>(false);

  const connectionStatusQuery = useQuery({
    queryKey: ["browserAgentConnectionStatus"],
    queryFn: () => apiClient.browserAgentConnectionStatus(),
    enabled: false, // Don't run automatically
    retry: false,
    onSuccess: response => {
      if (response.data.is_connected) {
        handleCreateDataSource();
      } else {
        setErrorMessage("Please make sure the Coefficient extension is installed and logged in.");
      }
    },
    onError: () => {
      setErrorMessage("Failed to check connection status. Please try again.");
    },
  });

  const handleCheckConnectionStatus = () => {
    setErrorMessage("");
    connectionStatusQuery.refetch();
  };

  const handleCreateDataSource = async () => {
    setIsCreatingDataSource(true);
    setErrorMessage("");
    try {
      const isSharedWithDomain = dataSource?.is_shared_with_domain ?? false;
      const dataSourceBody: DataSource = {
        ...dataSource,
        data_source_type: DataSourceType.Linkedin,
        data_source_name:
          dataSource?.data_source_name || DataSourceTypeDisplayNames[DataSourceType.Linkedin],
        credentials: {},
        ppress_metadata: {},
        is_shared_with_domain: isSharedWithDomain,
      };

      const response = await (isEditConnectionId
        ? apiClient.updateDataSource({
            data_source_id: isEditConnectionId,
            update_data_source_request: {
              data_source: dataSourceBody,
            },
          })
        : apiClient.createDataSource({
            create_data_source_request: {
              data_source: dataSourceBody,
            },
          }));

      const upsertedDataSource = response.data.data_source;

      track(isEditConnectionId ? "conn_edit_done" : "conn_create_done", {
        import_data_source: "linkedin",
        data_source_id: isEditConnectionId || upsertedDataSource.data_source_id,
      });

      if (onComplete) {
        onComplete(upsertedDataSource);
      }
    } catch (error) {
      track(isEditConnectionId ? "conn_edit_failed" : "conn_create_failed", {
        import_data_source: "linkedin",
        data_source_id: isEditConnectionId,
        reason: error,
      });

      setErrorMessage(
        isEditConnectionId
          ? "Failed to save connection. Please try again."
          : "Failed to create connection. Please try again."
      );
    } finally {
      setIsCreatingDataSource(false);
    }
  };

  const renderStep = () => {
    return (
      <StyledSteps direction="vertical" size="small" current={0}>
        <Steps.Step
          description={
            <StyledStepContainer>
              <StyledText>
                Install the Coefficient browser extension from{" "}
                <a href={browserAgentExtensionUrl} target="_blank" rel="noopener noreferrer">
                  Chrome Web Store
                </a>
                .
              </StyledText>
              <ConfirmCheckbox
                checked={userConfirmExtensionInstalled}
                onChange={e => {
                  setUserConfirmExtensionInstalled(e.target.checked);
                }}
              >
                <Typography>Extension is installed</Typography>
              </ConfirmCheckbox>
              <ButtonContainer>
                {connectionStatusQuery.isFetching || isCreatingDataSource ? (
                  <LoaderWithPerfTimings
                    name="linkedin_connection_status"
                    onScreenTimeOptions={{ dataSourceType: DataSourceType.Linkedin }}
                    style={{ marginTop: "12px" }}
                  />
                ) : (
                  <StyledButton
                    type="link"
                    onClick={handleCheckConnectionStatus}
                    disabled={!userConfirmExtensionInstalled}
                  >
                    Connect
                  </StyledButton>
                )}
              </ButtonContainer>
              {errorMessage && (
                <ErrorMessage
                  screen="linkedin_authorize"
                  message={errorMessage}
                  variant="error"
                  noMargin={true}
                />
              )}
            </StyledStepContainer>
          }
        />
      </StyledSteps>
    );
  };

  const HelperMessage: React.FC = () => {
    return (
      <Card className="coeblue" style={{ padding: 12, margin: "0 16px" }}>
        <div style={{ textAlign: "center" }}>
          <Typography
            fontWeight={700}
            color="textPrimary"
            align="center"
            style={{ marginBottom: 8 }}
          >
            Need help connecting?
          </Typography>
          <StyledAnchor
            href={"https://coefficient.io/help-center/linkedin"}
            target="_blank"
            fontWeight={"normal"}
          >
            Here's a step-by-step help guide
          </StyledAnchor>
        </div>
      </Card>
    );
  };

  return (
    <>
      <StyledContent $hidePageHeader={false}>
        <Graphics
          title="Let's connect to LinkedIn"
          description={
            <span>
              Extracting data from LinkedIn requires our Chrome extension that let's you extract
              data from LinkedIn and any site.
            </span>
          }
          graphics={
            <div className="graphicsLogo">
              <ConnectPlugIcon />
              <LinkedIn className="datasourceLogo" />
            </div>
          }
        />
        {renderStep()}
      </StyledContent>

      <StyledFooter>
        <HelperMessage />
        <SecurityInfo />
      </StyledFooter>
    </>
  );
};

export { AuthorizeLinkedInContent };
